import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ItemsGrid = ({ items = [], selectedItems = [], onSelectionChange, onEdit, onDelete }) => {
  const handleSelectItem = (itemId) => {
    if (selectedItems?.includes(itemId)) {
      onSelectionChange(selectedItems?.filter(id => id !== itemId));
    } else {
      onSelectionChange([...selectedItems, itemId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems?.length === items?.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(items?.map(item => item?.id));
    }
  };

  const formatCondition = (condition) => {
    return condition?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase()) || 'N/A';
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'mint': return 'text-emerald-600 bg-emerald-50';
      case 'near-mint': return 'text-green-600 bg-green-50';
      case 'excellent': return 'text-blue-600 bg-blue-50';
      case 'very-good': return 'text-cyan-600 bg-cyan-50';
      case 'good': return 'text-yellow-600 bg-yellow-50';
      case 'fair': return 'text-orange-600 bg-orange-50';
      case 'poor': return 'text-red-600 bg-red-50';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (items?.length === 0) {
    return null;
  }

  return (
    <div>
      {/* Selection Header */}
      <div className="flex items-center justify-between mb-4 p-3 bg-muted/20 rounded-lg">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={selectedItems?.length === items?.length && items?.length > 0}
            onChange={handleSelectAll}
            className="w-4 h-4 text-primary bg-background border border-border rounded focus:ring-primary focus:ring-2"
          />
          <span className="text-sm font-medium text-foreground">
            {selectedItems?.length > 0 
              ? `${selectedItems?.length} of ${items?.length} selected`
              : `${items?.length} items`
            }
          </span>
        </div>

        {selectedItems?.length > 0 && (
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Edit2"
              disabled={selectedItems?.length !== 1}
              onClick={() => {
                const item = items?.find(i => i?.id === selectedItems?.[0]);
                if (item) onEdit(item);
              }}
            >
              Edit
            </Button>
            <Button
              variant="destructive"
              size="sm"
              iconName="Trash2"
              onClick={() => {
                const itemsToDelete = items?.filter(i => selectedItems?.includes(i?.id));
                if (itemsToDelete?.length === 1) {
                  onDelete(itemsToDelete?.[0]);
                }
              }}
            >
              Delete
            </Button>
          </div>
        )}
      </div>
      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {items?.map((item) => (
          <div
            key={item?.id}
            className={`bg-card border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group ${
              selectedItems?.includes(item?.id) ? 'ring-2 ring-primary border-primary' : 'border-border'
            }`}
          >
            {/* Selection Checkbox */}
            <div className="absolute top-2 left-2 z-10">
              <input
                type="checkbox"
                checked={selectedItems?.includes(item?.id)}
                onChange={() => handleSelectItem(item?.id)}
                className="w-4 h-4 text-primary bg-background border border-border rounded focus:ring-primary focus:ring-2"
                onClick={(e) => e?.stopPropagation()}
              />
            </div>

            {/* Item Image */}
            <div className="aspect-square bg-muted relative overflow-hidden">
              {item?.primary_image_url ? (
                <img
                  src={item?.primary_image_url}
                  alt={item?.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon 
                    name="Package" 
                    size={48} 
                    className="text-muted-foreground"
                  />
                </div>
              )}
              
              {/* Quick Actions Overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center space-x-2">
                <Button
                  size="sm"
                  variant="secondary"
                  iconName="Eye"
                  className="bg-background/80 backdrop-blur-sm"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  iconName="Edit2"
                  className="bg-background/80 backdrop-blur-sm"
                  onClick={(e) => {
                    e?.stopPropagation();
                    onEdit(item);
                  }}
                />
              </div>

              {/* Featured Badge */}
              {item?.is_featured && (
                <div className="absolute top-2 right-2">
                  <div className="bg-warning text-warning-foreground px-2 py-1 rounded-full text-xs font-medium">
                    <Icon name="Star" size={12} className="inline mr-1" />
                    Featured
                  </div>
                </div>
              )}

              {/* For Sale Badge */}
              {item?.is_for_sale && (
                <div className="absolute bottom-2 right-2">
                  <div className="bg-success text-success-foreground px-2 py-1 rounded-full text-xs font-medium">
                    For Sale
                  </div>
                </div>
              )}
            </div>

            {/* Item Info */}
            <div className="p-4">
              <div className="mb-2">
                <h3 className="font-semibold text-card-foreground line-clamp-1 mb-1">
                  {item?.name}
                </h3>
                {item?.brand && (
                  <p className="text-sm text-muted-foreground">
                    {item?.brand} {item?.model && `• ${item?.model}`} {item?.year && `• ${item?.year}`}
                  </p>
                )}
              </div>

              {/* Condition */}
              <div className="mb-3">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getConditionColor(item?.condition)}`}>
                  {formatCondition(item?.condition)}
                </span>
              </div>

              {/* Values */}
              <div className="space-y-2 mb-3">
                {item?.current_value && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Current Value</span>
                    <span className="font-semibold text-success">
                      ${parseFloat(item?.current_value)?.toLocaleString()}
                    </span>
                  </div>
                )}
                
                {item?.purchase_price && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Purchase Price</span>
                    <span className="font-medium text-card-foreground">
                      ${parseFloat(item?.purchase_price)?.toLocaleString()}
                    </span>
                  </div>
                )}

                {item?.current_value && item?.purchase_price && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Gain/Loss</span>
                    <span className={`font-medium flex items-center ${
                      parseFloat(item?.current_value) >= parseFloat(item?.purchase_price) 
                        ? 'text-success' :'text-destructive'
                    }`}>
                      <Icon 
                        name={parseFloat(item?.current_value) >= parseFloat(item?.purchase_price) ? "TrendingUp" : "TrendingDown"} 
                        size={12} 
                        className="mr-1" 
                      />
                      {parseFloat(item?.current_value) >= parseFloat(item?.purchase_price) ? '+' : ''}
                      ${(parseFloat(item?.current_value) - parseFloat(item?.purchase_price))?.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t border-border">
                <div className="flex items-center">
                  <Icon name="Calendar" size={12} className="mr-1" />
                  {formatDate(item?.created_at)}
                </div>
                
                {item?.rarity_score && (
                  <div className="flex items-center">
                    <Icon name="Award" size={12} className="mr-1" />
                    Rarity {item?.rarity_score}/10
                  </div>
                )}
              </div>

              {/* Tags */}
              {item?.tags && item?.tags?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {item?.tags?.slice(0, 3)?.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                  {item?.tags?.length > 3 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
                      +{item?.tags?.length - 3}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ItemsGrid;